import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Manual, ManualPage } from '../../types/manual';
import { apiClient, manualsAPI } from '../../services/api';

interface ManualsState {
  manuals: Manual[];
  currentManual: Manual | null;
  currentPages: ManualPage[];
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
}

const initialState: ManualsState = {
  manuals: [],
  currentManual: null,
  currentPages: [],
  isLoading: false,
  isUploading: false,
  error: null,
};

export const fetchManuals = createAsyncThunk(
  'manuals/fetchManuals',
  async (machineId: string | undefined, { rejectWithValue }) => {
    try {
      const url = machineId ? `/machines/${machineId}/manuals` : '/manuals';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch manuals');
    }
  }
);

export const fetchManual = createAsyncThunk(
  'manuals/fetchManual',
  async (id: string, { rejectWithValue }) => {
    try {
      console.log('Fetching manual for ID:', id);
      const response = await apiClient.get(`/manuals/${id}/viewer`);
      console.log('Manual API response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Manual fetch error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch manual');
    }
  }
);

export const uploadPDFManual = createAsyncThunk(
  'manuals/uploadPDFManual',
  async (params: { machineId: string; title: string; file: File }, { rejectWithValue }) => {
    try {
      console.log('[DEBUG] Redux action: using File directly', {
        fileName: params.file.name,
        fileType: params.file.type,
        fileSize: params.file.size,
        constructor: params.file.constructor.name
      });

      const formData = new FormData();
      formData.append('pdf', params.file);
      formData.append('title', params.title);
      formData.append('type', 'pdf');

      console.log('[DEBUG] FormData created with direct File object, sending request');

      // API client interceptor will handle FormData Content-Type automatically
      const response = await apiClient.post(`/machines/${params.machineId}/manuals/pdf`, formData);
      return response.data.manual;
    } catch (error: any) {
      console.error('[ERROR] Redux action failed:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to upload PDF');
    }
  }
);

export const createPhotoManual = createAsyncThunk(
  'manuals/createPhotoManual',
  async (params: { machineId: string; title: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/machines/${params.machineId}/manuals/photo`, {
        title: params.title,
        type: 'photo',
      });
      
      // Log the response to understand its structure
      console.log('[DEBUG] createPhotoManual API response:', response.data);
      
      // The API might return the manual directly or nested in a 'manual' property
      // Try both possibilities
      const manual = response.data.manual || response.data;
      
      console.log('[DEBUG] Extracted manual:', manual);
      
      return manual;
    } catch (error: any) {
      console.error('[ERROR] createPhotoManual failed:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create photo manual');
    }
  }
);

export const uploadManualPages = createAsyncThunk(
  'manuals/uploadManualPages',
  async (params: { manualId: string; files: File[] }, { rejectWithValue }) => {
    try {
      console.log('[DEBUG] uploadManualPages: Starting upload for manual:', params.manualId);
      console.log('[DEBUG] uploadManualPages: Files to upload:', params.files.map(f => ({ 
        name: f.name, 
        size: f.size, 
        type: f.type 
      })));

      const response = await manualsAPI.uploadPages(params.manualId, params.files);
      
      console.log('[DEBUG] uploadManualPages: Upload response:', response.data);
      console.log('[DEBUG] uploadManualPages: Pages returned:', response.data.pages);
      
      return response.data.pages;
    } catch (error: any) {
      console.error('[ERROR] uploadManualPages: Upload failed:', error);
      console.error('[ERROR] uploadManualPages: Error response:', error.response);
      console.error('[ERROR] uploadManualPages: Error data:', error.response?.data);
      console.error('[ERROR] uploadManualPages: Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to upload pages';
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteManual = createAsyncThunk(
  'manuals/deleteManual',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/manuals/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete manual');
    }
  }
);

export const reorderPages = createAsyncThunk(
  'manuals/reorderPages',
  async (params: { manualId: string; pageIds: string[] }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/manuals/${params.manualId}/pages/reorder`, {
        page_ids: params.pageIds,
      });
      return response.data.pages;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reorder pages');
    }
  }
);

const manualsSlice = createSlice({
  name: 'manuals',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentManual: (state) => {
      state.currentManual = null;
      state.currentPages = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch manuals
    builder
      .addCase(fetchManuals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManuals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.manuals = action.payload;
      })
      .addCase(fetchManuals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch manual
    builder
      .addCase(fetchManual.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchManual.fulfilled, (state, action) => {
        state.isLoading = false;
        // The API returns a ManualViewerResponse directly, not wrapped in {manual, pages}
        const manualData = action.payload;
        console.log('Processing manual data in reducer:', manualData);

        const pages = manualData.pages || [];
        const processedManual = {
          id: manualData.id,
          machines: manualData.machines || [],
          title: manualData.title,
          type: manualData.type,
          page_count: manualData.page_count,
          created_at: manualData.created_at,
          updated_at: manualData.updated_at || manualData.created_at, // use updated_at if available
          pages: pages, // Attach pages to the manual object
        };

        console.log('Setting currentManual with pages to:', processedManual);
        state.currentManual = processedManual;
        state.currentPages = pages;
        state.error = null; // Clear any previous errors
      })
      .addCase(fetchManual.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Upload PDF
    builder
      .addCase(uploadPDFManual.pending, (state) => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(uploadPDFManual.fulfilled, (state, action) => {
        state.isUploading = false;
        if (action.payload) {
          state.manuals.unshift(action.payload);
        }
        state.error = null;
      })
      .addCase(uploadPDFManual.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string || 'PDF upload failed';
      });

    // Create photo manual
    builder
      .addCase(createPhotoManual.pending, (state) => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(createPhotoManual.fulfilled, (state, action) => {
        state.isUploading = false;
        const newManual = { ...action.payload, pages: [] }; // Initialize with empty pages
        state.manuals.unshift(newManual);
        state.currentManual = newManual;
        state.currentPages = [];
      })
      .addCase(createPhotoManual.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      });

    // Upload pages
    builder
      .addCase(uploadManualPages.pending, (state) => {
        state.isUploading = true;
      })
      .addCase(uploadManualPages.fulfilled, (state, action) => {
        state.isUploading = false;
        const newPages = [...state.currentPages, ...action.payload];
        state.currentPages = newPages;
        if (state.currentManual) {
          state.currentManual.page_count = newPages.length;
          state.currentManual.pages = newPages; // Keep pages in sync
        }
      })
      .addCase(uploadManualPages.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      });

    // Delete manual
    builder
      .addCase(deleteManual.fulfilled, (state, action) => {
        state.manuals = state.manuals.filter(m => m.id !== action.payload);
        if (state.currentManual?.id === action.payload) {
          state.currentManual = null;
          state.currentPages = [];
        }
      });

    // Reorder pages
    builder
      .addCase(reorderPages.fulfilled, (state, action) => {
        state.currentPages = action.payload;
        if (state.currentManual) {
          state.currentManual.pages = action.payload; // Keep pages in sync
        }
      });
  },
});

// Create alias for consistency with the manual detail page
export const fetchManualViewer = fetchManual;

export const { clearError, clearCurrentManual } = manualsSlice.actions;
export default manualsSlice.reducer;